import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { localRect, localViewport } from '../../utils/zoom';

/**
 * Anchored popover: renders to document.body via a portal (the rail's
 * overflow:hidden would clip it otherwise) and positions itself to the RIGHT
 * of `anchorEl`, flipping to the left when it would leave the viewport and
 * clamping vertically. Closes on outside mousedown and Escape.
 */
const Popover = ({ open, anchorEl, onClose, children, width = 340 }) => {
  const panelRef = useRef(null);
  const [pos, setPos] = useState({ top: -9999, left: -9999 });

  useLayoutEffect(() => {
    if (!open || !anchorEl) return undefined;

    const place = () => {
      const r = localRect(anchorEl);
      const vp = localViewport();
      const panelH = panelRef.current ? panelRef.current.offsetHeight : 0;
      let left = r.right + 10;
      if (left + width + 12 > vp.width) {
        left = Math.max(12, r.left - width - 10);
      }
      // Align the panel's bottom near the trigger (it sits at the rail bottom),
      // then clamp fully inside the viewport.
      let top = r.bottom - panelH;
      top = Math.max(12, Math.min(top, vp.height - panelH - 12));
      setPos({ top, left });
    };

    place();
    // Re-measure after first paint (panel height is unknown before render).
    const raf = requestAnimationFrame(place);
    // The panel's content loads asynchronously and can grow well after mount —
    // re-place whenever its size changes, or it ends up clipped off-screen.
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(place) : null;
    if (observer && panelRef.current) observer.observe(panelRef.current);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      cancelAnimationFrame(raf);
      if (observer) observer.disconnect();
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, anchorEl, width]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    const onDown = (e) => {
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      if (anchorEl && anchorEl.contains(e.target)) return;
      onClose && onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, anchorEl, onClose]);

  if (!open || !anchorEl) return null;

  return ReactDOM.createPortal(
    <div
      ref={panelRef}
      role="dialog"
      style={{
        position: 'fixed', top: pos.top, left: pos.left, width, zIndex: 1200,
        background: 'var(--surface-solid)',
        border: '1px solid var(--line)', borderRadius: 18,
        boxShadow: 'var(--shadow-glass)', padding: 18,
        fontFamily: 'var(--font-sans)',
        maxHeight: 'calc(100vh / var(--app-zoom, 1) - 24px)', overflowY: 'auto',
      }}
    >
      {children}
    </div>,
    document.body,
  );
};

export default Popover;
